import React from "react";
//#region redux
import { setHasMissingPaymentDetails } from "store/actions/authActions";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
//#endregion
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { DocumentFormTypes } from "models/formTypes";
import { CommonEnums, StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { DocumentDraftCreateFormContainerProps } from "models/propTypes/documentDraftPropTypes";
import { Person } from "models/stateTypes/authState";
//#endregion
//#region components
import DocumentCreateForm from "./createForm";
import useCreateFormHelpers from "./useCreateFormHelpers";
import { LoadingWithLogo, useFloatingMessages } from "components/common";
import {
  costForwardingValidationFields,
  getCostForwardingFormValues,
  saveMarkingAfterCreate,
} from "components/costForwardingMarking";
import {
  useDocumentService,
  useIncomingInvoiceService,
  useReceiptService,
  useDocumentDraftService,
  useGenericDocumentService,
  useCostForwardingService,
} from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, SessionHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const DocumentCreateFormContainer: React.ForwardRefRenderFunction<
  FormHandles,
  DocumentDraftCreateFormContainerProps
> = (props, ref) => {
  const documentService = useDocumentService();
  const incomingInvoiceService = useIncomingInvoiceService();
  const receiptService = useReceiptService();
  const documentDraftService = useDocumentDraftService();
  const genericDocumentService = useGenericDocumentService();
  const { putMarking } = useCostForwardingService();
  const { enqueueMessage } = useFloatingMessages();

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const {
    initialValues,
    validationSchema,
    onReceiptCategorySelect,
    onReceiptCategoryClear,
    onAmountChange,
    onCurrencyChange,
    availableDocumentTypes,
    receiptCategoriesInputItems,
    receiptPaymentMethods,
    topReceiptPaymentMethods,
    receiptPaymentMethodActions,
    dimensionAmount,
    currency,
    isReceiptLoading,
    setIsReceiptLoading,
  } = useCreateFormHelpers();

  // The receipt has no merchant field here, so there is no name to prefill the description from.
  const defaultValues = React.useMemo(
    (): DocumentFormTypes.CreateNew => ({
      ...initialValues,
      ...getCostForwardingFormValues(undefined, true, undefined),
    }),
    [initialValues]
  );
  const validationSchemaWithCostForwarding = React.useMemo(
    () => validationSchema.shape(costForwardingValidationFields),
    [validationSchema]
  );

  // The shared error interceptor has already shown the server's reason for the rejected marking.
  const onCostForwardingNotSaved = () =>
    enqueueMessage(
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.CostForwarding.NotSavedAfterCreate),
      StyleEnums.DisplayType.Warning,
      true
    );

  const handleSubmit = async (values: DocumentFormTypes.CreateNew) => {
    let res;

    switch (values.type) {
      case CommonEnums.DocumentTypes.Receipt:
        updateHasMissingPaymentDetails(values.receipt.isManualPayment!);
        res = await saveMarkingAfterCreate(
          values,
          () => receiptService.create(documentDraftService.formToReceiptPost(values)),
          (created) => created.data,
          (receiptId, marking) => putMarking("receipt", receiptId, marking),
          onCostForwardingNotSaved
        );
        break;
      case CommonEnums.DocumentTypes.IncomingInvoice:
        res = await incomingInvoiceService.create(documentDraftService.formToIncomingInvoicePost(values));
        break;
      case CommonEnums.DocumentTypes.GenericDocument:
        res = await genericDocumentService.create(documentDraftService.formToGenericDocumentPost(values));
        break;
      case CommonEnums.DocumentTypes.SIEFiles:
        res = await documentService.uploadSIEFile(documentService.formToSIEFilePost(values));
        break;
      case CommonEnums.DocumentTypes.SelfInvoice:
        res = await documentService.uploadSelfInvoice(documentService.formToSelfInvoiceFilePost(values));
        break;
    }

    return { type: values.type, res };
  };

  const updateHasMissingPaymentDetails = (updatedValue: boolean) => {
    const userSession = SessionHelper.getSessionItem<Person>("user_session");
    if (userSession.hasMissingPaymentDetails !== updatedValue) {
      userSession.hasMissingPaymentDetails = updatedValue;
    }
    SessionHelper.saveToSession("user_session", userSession);

    dispatch(setHasMissingPaymentDetails(updatedValue));
  };

  return (
    <>
      {isReceiptLoading && (
        <div className={cssExports.dialogLoadingContainer}>
          <LoadingWithLogo />
        </div>
      )}
      <DocumentCreateForm
        ref={ref}
        defaultValues={defaultValues}
        validationSchema={validationSchemaWithCostForwarding}
        handleSubmit={handleSubmit}
        onFormValidationChange={props.onFormValidationChange}
        receiptCategorySelectHandler={onReceiptCategorySelect}
        receiptCategoryClearHandler={onReceiptCategoryClear}
        amountChangeHandler={onAmountChange}
        currencyChangeHandler={onCurrencyChange}
        availableDocumentTypes={availableDocumentTypes}
        receiptCategories={receiptCategoriesInputItems}
        receiptPaymentMethods={receiptPaymentMethods}
        topReceiptPaymentMethods={topReceiptPaymentMethods}
        receiptPaymentMethodActions={receiptPaymentMethodActions}
        dimensionAmount={dimensionAmount}
        currency={currency}
        isReceiptLoading={isReceiptLoading}
        setIsReceiptLoading={setIsReceiptLoading}
        allowCostForwarding
      />
    </>
  );
};

export default React.forwardRef(DocumentCreateFormContainer);
